import { INTERVIEW_TYPES } from '../constants/interview';

const STATUS_STYLES = {
  scheduled: 'bg-yellow-100 text-yellow-900 border-yellow-300',
  reschedule_requested: 'bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] border-amber-300',
  cancelled: 'bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))] border-red-200',
  confirmed: 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))] border-green-300'
};

export default function InterviewScheduleCard({ app, compact = false }) {
  const sched = app.interviewSchedule;
  const display = app.interviewScheduleDisplay;
  if (!sched) return null;

  const typeLabel =
    app.interviewTypeLabel ||
    INTERVIEW_TYPES.find(t => t.code === sched.interviewType)?.label ||
    sched.interviewType;

  const statusClass = STATUS_STYLES[sched.status] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]';

  return (
    <div
      className={`border-2 ${compact ? 'p-3' : 'p-4'} ${statusClass} grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4`}
    >
      <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] text-center min-w-[5.5rem] shadow-sm">
        <div className="bg-[hsl(var(--ds-secondary))] text-white text-[10px] uppercase tracking-wide py-1 px-2">
          {display?.dateLabel
            ? new Date(sched.scheduledAt).toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()
            : 'INT'}
        </div>
        <div className="text-2xl font-bold text-[hsl(var(--ds-fg))] py-1">
          {display?.dateLabel
            ? new Date(sched.scheduledAt).getDate()
            : '—'}
        </div>
        <div className="text-xs text-[hsl(var(--ds-muted-fg))] pb-2 px-1">
          {display?.timeLabel || '—'}
        </div>
      </div>

      <div className="text-sm space-y-1.5 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-[hsl(var(--ds-fg))]">
            {sched.status === 'confirmed' ? 'Interview accepted' : 'Interview scheduled'}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 border ${statusClass}`}>
            {app.interviewStatusLabel || sched.status}
          </span>
        </div>
        <p>
          <span className="text-[hsl(var(--ds-muted-fg))]">Type:</span> {typeLabel}
        </p>
        {display?.dateLabel && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">When:</span> {display.dateLabel} at {display.timeLabel}
            {sched.timezone ? ` (${sched.timezone})` : ''}
          </p>
        )}
        {sched.durationMinutes && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">Duration:</span> {sched.durationMinutes} min
          </p>
        )}
        {sched.interviewType === 'virtual' && sched.meetingLink && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">Link:</span>{' '}
            <a href={sched.meetingLink} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--ds-primary))] hover:underline break-all">
              {sched.meetingLink}
            </a>
          </p>
        )}
        {sched.interviewType === 'in_person' && sched.location && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">Location:</span> {sched.location}
          </p>
        )}
        {sched.panelists && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">Panel:</span> {sched.panelists}
          </p>
        )}
        {sched.dressCode && (
          <p>
            <span className="text-[hsl(var(--ds-muted-fg))]">Dress:</span> {sched.dressCode}
          </p>
        )}
        {sched.instructions && (
          <p className="text-[hsl(var(--ds-fg))] whitespace-pre-wrap border-t border-[hsl(var(--ds-border))]/60 pt-2 mt-2">
            {sched.instructions}
          </p>
        )}
        {sched.status === 'reschedule_requested' && sched.rescheduleRequest && (
          <div className="text-amber-900 bg-amber-50/80 border border-amber-200 p-2 text-xs mt-2 space-y-1">
            <p>
              <strong>Reschedule requested:</strong>{' '}
              {new Date(sched.rescheduleRequest.proposedScheduledAt).toLocaleString()}
              {sched.rescheduleRequest.message ? ` — ${sched.rescheduleRequest.message}` : ''}
            </p>
            {app.rescheduleApprovalSummary && (
              <p>
                <strong>Approvals:</strong>{' '}
                {app.rescheduleApprovalSummary.allApproved
                  ? 'All parties approved — pending confirmation'
                  : `Waiting for ${app.rescheduleApprovalSummary.pendingLabels.join(', ')}`}
              </p>
            )}
          </div>
        )}
        {sched.status === 'cancelled' && sched.cancelReason && (
          <p className="text-red-800 text-xs mt-2">
            <strong>Cancelled:</strong> {sched.cancelReason}
          </p>
        )}
      </div>
    </div>
  );
}
